import React, { useState, useEffect, useRef, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import CompanyTargetstChart from "../../components/dashboard/Diagrams/CompanyTargetsChart/CompanyTargetsChart";
import BookedOrdersChart from "../../components/dashboard/Diagrams/BookedOrdersChart/BookedOrdersChart";
import IndividualTargetsChart from "../../components/dashboard/Diagrams/IndividualTargetsChart/IndividualTargetsChart";
import BookedOrdersBarChart from "../../components/dashboard/Diagrams/BookedOrdersBarChart/BookedOrdersBarChart";
import {
  useGetNotificationsByUserIdQuery,
  useClearNotificationMutation,
} from "../../features/notification/notificationSlice";
import { useGetOrdersSummeryByUserIdQuery } from "../../features/order/orderSlice";
import useInterval from "../../hooks/UseInterval";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../features/auth/authSlice";
import HighStadiumRoar from "../../assets/sounds/HighStadiumRoar.mp3";
import LowCrowdClap from "../../assets/sounds/LowCrowdClap.mp3";
import MidCrowdCheer from "../../assets/sounds/MidCrowdCheer.mp3";
import ChampionsClip from "../../assets/sounds/ChampionsClip.mp3";

const notificationSoundMap = {
  Crowd_Clap: LowCrowdClap,
  Crowd_Cheer: MidCrowdCheer,
  Stadium_Roar: HighStadiumRoar,
};

const notificationSoundPriority = {
  Stadium_Roar: 3,
  Crowd_Cheer: 2,
  Crowd_Clap: 1,
};

const DiagramsFullScreen = () => {
  const user = useSelector(selectCurrentUser);
  const [chartWidth, setChartWidth] = useState(560);
  const [chartHeight, setChartHeight] = useState(560);
  const sliderRef = useRef(null);
  const previousNotificationIdsRef = useRef(new Set());
  const soundQueueRef = useRef([]);
  const queuedNotificationIdsRef = useRef(new Set());
  const isProcessingSoundQueueRef = useRef(false);
  const [newNotifications, setNewNotifications] = useState([]);
  const [teamTodayAchieved, setTeamTodayAchieved] = useState(false);
  const [teamWeekAchieved, setTeamWeekAchieved] = useState(false);

  const {
    data: notificationsData,

    refetch,
  } = useGetNotificationsByUserIdQuery(user.userID);

  const [clearAllNotification] = useClearNotificationMutation();

  const { data: orderSummaryToday, refetch: orderRefetchToday } =
    useGetOrdersSummeryByUserIdQuery({
      userId: 5,
      type: "today",
      startDate: "null",
      endDate: "null",
    });

  const { data: orderSummaryWeek, refetch: orderRefetchWeek } =
    useGetOrdersSummeryByUserIdQuery({
      userId: 5,
      type: "week",
      startDate: "null",
      endDate: "null",
    });

  const [teamSummaryDataToday, setTeamSummaryDataToday] = useState({
    current: 0,
    target: 0,
  });
  const [teamSummaryDataWeek, setTeamSummaryDataWeek] = useState({
    current: 0,
    target: 0,
  });

  useInterval(() => {
    refetch();
    orderRefetchToday();
    orderRefetchWeek();
  }, 10000);

  useEffect(() => {
    if (notificationsData) {
      const newNotificationsData = notificationsData.ids.map((id) => {
        return notificationsData.entities[id];
      });
      const previousIds = previousNotificationIdsRef.current;
      const newlyAddedNotifications = newNotificationsData.filter(
        (notification) => !previousIds.has(notification.notificationId),
      );
      if (newlyAddedNotifications.length > 0) {
        setNewNotifications(newlyAddedNotifications);
      } else {
        setNewNotifications([]);
      }
      previousNotificationIdsRef.current = new Set(
        newNotificationsData.map((notification) => notification.notificationId),
      );
    }
  }, [notificationsData]);

  useEffect(() => {
    if (orderSummaryToday) {
      setTeamSummaryDataToday({
        current: orderSummaryToday.teamAchievement,
        target: orderSummaryToday.teamTarget,
      });
    }
  }, [orderSummaryToday]);

  useEffect(() => {
    if (orderSummaryWeek) {
      setTeamSummaryDataWeek({
        current: orderSummaryWeek.teamAchievement,
        target: orderSummaryWeek.teamTarget,
      });
    }
  }, [orderSummaryWeek]);

  const clearNotificationsByIds = useCallback(
    async (notificationIds) => {
      if (!notificationIds || notificationIds.length === 0) return;

      try {
        await clearAllNotification({
          notificationIds,
        }).unwrap();
      } catch (err) {
        console.error("Failed to update the Notifications", err);
      }
    },
    [clearAllNotification],
  );

  const playSingleSound = useCallback(async (soundFile) => {
    try {
      const audio = new Audio(soundFile);
      await audio.play();
      return true;
    } catch (err) {
      console.warn("Notification sound playback was blocked or failed.", err);
      return false;
    }
  }, []);

  const processSoundQueue = useCallback(async () => {
    if (isProcessingSoundQueueRef.current) return;

    isProcessingSoundQueueRef.current = true;
    const playedNotificationIds = [];

    try {
      while (soundQueueRef.current.length > 0) {
        const queueItem = soundQueueRef.current.shift();
        const played = await playSingleSound(queueItem.soundFile);

        queuedNotificationIdsRef.current.delete(queueItem.notificationId);
        if (played) {
          playedNotificationIds.push(queueItem.notificationId);
        }
      }
    } finally {
      isProcessingSoundQueueRef.current = false;
    }

    if (playedNotificationIds.length > 0) {
      await clearNotificationsByIds(playedNotificationIds);
    }
  }, [clearNotificationsByIds, playSingleSound]);

  useEffect(() => {
    if (newNotifications.length <= 0 || user.userRole !== "Dashboard") return;

    const prioritizedPlayableNotifications = newNotifications
      .map((notification) => ({
        notificationId: notification.notificationId,
        soundFile: notificationSoundMap[notification.message],
        priority: notificationSoundPriority[notification.message] || 0,
      }))
      .filter((item) => !!item.soundFile)
      .sort((a, b) => b.priority - a.priority);

    for (const queueItem of prioritizedPlayableNotifications) {
      if (
        !queuedNotificationIdsRef.current.has(queueItem.notificationId)
      ) {
        soundQueueRef.current.push(queueItem);
        queuedNotificationIdsRef.current.add(queueItem.notificationId);
      }
    }

    processSoundQueue();
  }, [newNotifications, processSoundQueue, user.userRole]);

  useEffect(() => {
    return () => {
      soundQueueRef.current = [];
      queuedNotificationIdsRef.current.clear();
      isProcessingSoundQueueRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      teamSummaryDataToday.current >= teamSummaryDataToday.target &&
      teamSummaryDataToday.current > 0 &&
      teamSummaryDataToday.target > 0 &&
      !teamTodayAchieved &&
      user.userRole === "Dashboard" &&
      newNotifications.length > 0
    ) {
      const audio = new Audio(ChampionsClip);
      audio.play();
      setTeamTodayAchieved(true);
    }
  }, [
    teamSummaryDataToday,
    teamTodayAchieved,
    user.userRole,
    newNotifications.length,
  ]);

  useEffect(() => {
    if (
      teamSummaryDataWeek.current >= teamSummaryDataWeek.target &&
      teamSummaryDataWeek.current > 0 &&
      teamSummaryDataWeek.target > 0 &&
      !teamWeekAchieved &&
      user.userRole === "Dashboard" &&
      newNotifications.length > 0
    ) {
      const audio = new Audio(ChampionsClip);
      audio.play();
      setTeamWeekAchieved(true);
    }
  }, [
    teamSummaryDataWeek,
    teamWeekAchieved,
    user.userRole,
    newNotifications.length,
  ]);

  useEffect(() => {
    const chartContainer = document.getElementById("chartContainer");

    const handleResize = () => {
      setChartWidth(chartContainer.offsetWidth - 50);
      setChartHeight(chartContainer.offsetHeight - 170);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      sliderRef.current.slickNext();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(interval);
    };
  }, []);

  const generateComponents = (timeInterval) => {
    let components = [];

    if (
      timeInterval === "today" ||
      timeInterval === "week" ||
      timeInterval === "month"
    ) {
      components.push({
        component: (
          <BookedOrdersBarChart
            width={chartWidth}
            height={chartHeight}
            isAutomated={true}
            isDisabled={true}
            timeInterval={timeInterval}
            fontSize={16}
            margin={40}
          />
        ),
      });
    }

    if (timeInterval === "year" || timeInterval === "yearly") {
      components.push({
        component: (
          <BookedOrdersChart
            width={chartWidth}
            height={chartHeight}
            isAutomated={true}
            isDisabled={true}
            timeInterval={timeInterval}
            fontSize={16}
            margin={40}
          />
        ),
      });
    }

    if (timeInterval === "today") {
      components.push({
        component: (
          <CompanyTargetstChart
            width={chartWidth}
            height={chartHeight}
            isAutomated={true}
            isDisabled={true}
            timeInterval={timeInterval}
            fontSize={16}
            margin={40}
          />
        ),
      });
    }

    if (timeInterval === "month") {
      components.push({
        component: (
          <IndividualTargetsChart
            width={chartWidth}
            height={chartHeight}
            isAutomated={true}
            isDisabled={true}
            timeInterval={timeInterval}
            fontSize={16}
            margin={40}
          />
        ),
      });
    }

    return components;
  };

  const timeIntervals = [
    { key: "today", components: generateComponents("today") },
    { key: "week", components: generateComponents("week") },
    { key: "month", components: generateComponents("month") },
    { key: "year", components: generateComponents("year") },
    { key: "yearly", components: generateComponents("yearly") },
  ];

  const settings = {
    dots: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    pauseOnHover: true,
  };

  return (
    <div
      id="chartContainer"
      className="py-3 px-2 rounded"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      <Slider {...settings} ref={sliderRef}>
        {timeIntervals.map(({ key, components }) =>
          components.map((slide, index) => (
            <div className="slide" key={`${key}-${index}`}>
              <div
                className="chart-slide px-1 rounded"
                style={{ height: "100vh" }}
              >
                {slide.component}
              </div>
            </div>
          )),
        )}
      </Slider>
    </div>
  );
};

export default DiagramsFullScreen;
